import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  UserCheck, 
  BookOpen, 
  Settings, 
  TrendingUp,
  CheckCircle,
  XCircle,
  Trash2,
  Edit,
  FileCheck,
  Crown,
  Star
} from 'lucide-react';
import InstructorApplications from '@/components/admin/InstructorApplications';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const AdminPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [pendingInstructors, setPendingInstructors] = useState([]);
  const [settings, setSettings] = useState({});
  const [transactions, setTransactions] = useState({ transactions: [], summary: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, instructorsRes, pendingRes, settingsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/instructors`),
        axios.get(`${API}/admin/instructor-applications?status=pending`),
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/transactions`).catch(() => ({ data: { transactions: [], summary: {} } }))
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setInstructors(instructorsRes.data);
      setPendingInstructors(pendingRes.data);
      setSettings(settingsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        alert('Admin erişimi gerekli!');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInstructor = async (applicationId) => {
    try {
      await axios.post(`${API}/admin/instructor-application/${applicationId}/approve`);
      alert('Eğitmen onaylandı!');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to approve instructor:', error);
      alert('Onaylama başarısız!');
    }
  };

  const handleRejectInstructor = async (applicationId) => {
    try {
      await axios.post(`${API}/admin/instructor-application/${applicationId}/reject`);
      alert('Başvuru reddedildi!');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to reject instructor:', error);
      alert('Reddetme başarısız!');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      alert('Kullanıcı silindi!');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.detail || 'Silme başarısız!');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role: newRole });
      toast.success('Rol güncellendi!');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Güncelleme başarısız!');
    }
  };

  const handleUpdatePremium = async (userId, premiumTier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/premium`, { premium_tier: premiumTier });
      toast.success(`Kullanıcı ${premiumTier === 'pro' ? 'PRO' : premiumTier === 'plus' ? 'PLUS' : 'Ücretsiz'} üye yapıldı!`);
      fetchAdminData();
    } catch (error) {
      console.error('Failed to update premium:', error);
      toast.error(error.response?.data?.detail || 'Premium güncelleme başarısız!');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings);
      alert('Ayarlar kaydedildi!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Kaydetme başarısız!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Admin Kontrol Paneli</h1>
          <p className="text-gray-600">Pilatika platformunu yönetin</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Kullanıcı</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_users}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Eğitmenler</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_instructors}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bekleyen Başvurular</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending_instructors}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Ders</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_lessons}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rezervasyonlar</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_bookings}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="users">Kullanıcılar</TabsTrigger>
            <TabsTrigger value="instructors">Eğitmenler</TabsTrigger>
            <TabsTrigger value="applications">
              <FileCheck className="h-4 w-4 mr-1" />
              Eğitmen Başvuruları
            </TabsTrigger>
            <TabsTrigger value="pending">Bekleyen Başvurular ({pendingInstructors.length})</TabsTrigger>
            <TabsTrigger value="revenue">
              <TrendingUp className="h-4 w-4 mr-1" />
              Platform Geliri
            </TabsTrigger>
            <TabsTrigger value="settings">Ayarlar</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Kullanıcı Yönetimi</CardTitle>
                <CardDescription>Tüm kullanıcıları görüntüleyin ve yönetin</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ad Soyad</TableHead>
                      <TableHead>E-posta</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Kayıt Tarihi</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(user.created_at).toLocaleDateString('tr-TR')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {/* Premium & Rol Düzenleme */}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" title="Düzenle">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Kullanıcı Düzenle</DialogTitle>
                                  <DialogDescription>
                                    {user.full_name} ({user.email})
                                  </DialogDescription>
                                </DialogHeader>
                                
                                <div className="space-y-4 py-4">
                                  {/* Current Status */}
                                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                                    <span className="text-sm text-gray-600">Mevcut Durum:</span>
                                    <Badge variant={user.is_premium ? "default" : "secondary"}>
                                      {user.premium_tier === 'pro' ? '👑 PRO' : user.premium_tier === 'plus' ? '⭐ PLUS' : '🆓 Ücretsiz'}
                                    </Badge>
                                    <Badge variant="outline">{user.role}</Badge>
                                  </div>
                                  
                                  {/* Premium Tier Selection */}
                                  <div className="space-y-2">
                                    <Label className="text-sm font-medium">Premium Durumu</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                      <Button 
                                        variant={user.premium_tier === 'pro' ? "default" : "outline"}
                                        className="flex flex-col h-auto py-3"
                                        onClick={() => handleUpdatePremium(user.id, 'pro')}
                                      >
                                        <Crown className="h-5 w-5 mb-1 text-yellow-500" />
                                        <span className="text-xs">PRO</span>
                                      </Button>
                                      <Button 
                                        variant={user.premium_tier === 'plus' ? "default" : "outline"}
                                        className="flex flex-col h-auto py-3"
                                        onClick={() => handleUpdatePremium(user.id, 'plus')}
                                      >
                                        <Star className="h-5 w-5 mb-1 text-purple-500" />
                                        <span className="text-xs">PLUS</span>
                                      </Button>
                                      <Button 
                                        variant={!user.is_premium ? "default" : "outline"}
                                        className="flex flex-col h-auto py-3"
                                        onClick={() => handleUpdatePremium(user.id, 'free')}
                                      >
                                        <Users className="h-5 w-5 mb-1 text-gray-500" />
                                        <span className="text-xs">Ücretsiz</span>
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Role Selection */}
                                  {user.role !== 'admin' && (
                                    <div className="space-y-2">
                                      <Label className="text-sm font-medium">Rol</Label>
                                      <div className="grid grid-cols-2 gap-2">
                                        <Button 
                                          variant="outline"
                                          onClick={() => handleUpdateRole(user.id, 'admin')}
                                        >
                                          🔧 Admin Yap
                                        </Button>
                                        <Button 
                                          variant="outline"
                                          onClick={() => handleUpdateRole(user.id, 'instructor')}
                                        >
                                          👩‍🏫 Eğitmen Yap
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                            
                            {/* Delete User */}
                            {user.role !== 'admin' && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="destructive" size="sm" title="Sil">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {user.full_name} kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>İptal</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteUser(user.id)}>
                                      Sil
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Instructors Tab */}
          <TabsContent value="instructors">
            <Card>
              <CardHeader>
                <CardTitle>Eğitmen Yönetimi</CardTitle>
                <CardDescription>Onaylanmış eğitmenleri görüntüleyin</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ad Soyad</TableHead>
                      <TableHead>E-posta</TableHead>
                      <TableHead>Tip</TableHead>
                      <TableHead>Uzmanlıklar</TableHead>
                      <TableHead>Onay Tarihi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {instructors.map((instructor) => (
                      <TableRow key={instructor.user_id}>
                        <TableCell className="font-medium">{instructor.user?.full_name}</TableCell>
                        <TableCell>{instructor.user?.email}</TableCell>
                        <TableCell>
                          <Badge variant={instructor.instructor_type === 'ai' ? 'default' : 'secondary'}>
                            {instructor.instructor_type === 'ai' ? '🤖 AI' : '👨‍🏫 İnsan'}
                          </Badge>
                        </TableCell>
                        <TableCell>{instructor.specialties?.slice(0, 2).join(', ')}</TableCell>
                        <TableCell>{new Date(instructor.applied_at).toLocaleDateString('tr-TR')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Instructor Applications Tab - New detailed view */}
          <TabsContent value="applications">
            <InstructorApplications />
          </TabsContent>

          {/* Pending Instructors Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Bekleyen Eğitmen Başvuruları</CardTitle>
                <CardDescription>Eğitmen başvurularını onaylayın veya reddedin</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingInstructors.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Bekleyen başvuru bulunmuyor
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ad Soyad</TableHead>
                        <TableHead>E-posta</TableHead>
                        <TableHead>Biyografi</TableHead>
                        <TableHead>Uzmanlıklar</TableHead>
                        <TableHead>Başvuru Tarihi</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingInstructors.map((instructor) => (
                        <TableRow key={instructor.id}>
                          <TableCell className="font-medium">{instructor.user?.full_name}</TableCell>
                          <TableCell>{instructor.user?.email}</TableCell>
                          <TableCell className="max-w-xs truncate">{instructor.bio}</TableCell>
                          <TableCell>{instructor.specialties?.join(', ')}</TableCell>
                          <TableCell>{new Date(instructor.applied_at).toLocaleDateString('tr-TR')}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveInstructor(instructor.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Onayla
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleRejectInstructor(instructor.id)}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reddet
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <div className="space-y-6">
              {/* Revenue Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-green-800">Toplam Gelir</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-700">
                      ₺{(transactions.summary?.total_revenue || 0).toLocaleString('tr-TR')}
                    </div>
                    <p className="text-xs text-green-600 mt-1">Brüt satışlar</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-purple-800">Platform Payı (%20)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-700">
                      ₺{(transactions.summary?.total_commission || 0).toLocaleString('tr-TR')}
                    </div>
                    <p className="text-xs text-purple-600 mt-1">Platform komisyonu</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-blue-800">Eğitmen Payı (%80)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-700">
                      ₺{(transactions.summary?.total_instructor_earnings || 0).toLocaleString('tr-TR')}
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Eğitmenlere ödenen</p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-orange-800">Toplam Satış</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-700">
                      {transactions.summary?.total_transactions || 0}
                    </div>
                    <p className="text-xs text-orange-600 mt-1">Tamamlanan işlem</p>
                  </CardContent>
                </Card>
              </div>

              {/* Transactions Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Tüm İşlemler</CardTitle>
                  <CardDescription>Platform üzerinden gerçekleşen ders satışları ve gelir dağılımı</CardDescription>
                </CardHeader>
                <CardContent>
                  {transactions.transactions?.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Henüz işlem bulunmuyor</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Ders</TableHead>
                          <TableHead>Eğitmen</TableHead>
                          <TableHead className="text-right">Toplam</TableHead>
                          <TableHead className="text-right">Eğitmen Payı (%80)</TableHead>
                          <TableHead className="text-right">Platform Payı (%20)</TableHead>
                          <TableHead>Durum</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.transactions?.map((tx) => (
                          <TableRow key={tx.id}>
                            <TableCell className="text-sm">
                              {new Date(tx.created_at).toLocaleDateString('tr-TR')}
                            </TableCell>
                            <TableCell className="font-medium">{tx.lesson_id?.slice(0, 8)}...</TableCell>
                            <TableCell>{tx.instructor_id?.slice(0, 8)}...</TableCell>
                            <TableCell className="text-right font-semibold">
                              ₺{tx.total_amount?.toLocaleString('tr-TR')}
                            </TableCell>
                            <TableCell className="text-right text-blue-600 font-medium">
                              ₺{tx.instructor_amount?.toLocaleString('tr-TR')}
                            </TableCell>
                            <TableCell className="text-right text-purple-600 font-medium">
                              ₺{tx.commission_amount?.toLocaleString('tr-TR')}
                            </TableCell>
                            <TableCell>
                              <Badge className={tx.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                {tx.status === 'completed' ? 'Tamamlandı' : tx.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Platform Ayarları</CardTitle>
                <CardDescription>Platform genelinde uygulanan ayarları yönetin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="commission">Komisyon Oranı (%)</Label>
                  <Input
                    id="commission"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={(settings.commission_rate * 100).toFixed(0)}
                    onChange={(e) => setSettings({...settings, commission_rate: parseInt(e.target.value) / 100})}
                    className="mt-2"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    İnsan eğitmen satışlarından alınacak komisyon oranı
                  </p>
                </div>

                <div>
                  <Label htmlFor="platform_name">Platform Adı</Label>
                  <Input
                    id="platform_name"
                    value={settings.platform_name || ''}
                    onChange={(e) => setSettings({...settings, platform_name: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="support_email">Destek E-posta</Label>
                  <Input
                    id="support_email"
                    type="email"
                    value={settings.support_email || ''}
                    onChange={(e) => setSettings({...settings, support_email: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <Button onClick={handleSaveSettings} className="w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Ayarları Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
